// InformationRequestCard — the agent's structured question, as an INTERACTIVE
// inline card, now STEPPED.
//
// .claude/analysis/agent-form-primitive-design.md §3: the agent poses a short
// TYPED form inline in the transcript; the author answers it in place. A form is
// an ordered list of STEPS (agent-form-primitive-design §1) — a short form is
// projected into QUESTIONS (formQuestions.ts, D42): a one-question form renders
// flat; a longer one shows ONE question at a time with a "2 / 5" indicator and
// Back / Skip / Continue, so the author holds one question in working memory at
// a time (Hick / cognitive-load) and the card stays compact in the chat column. This is the DURABLE half of
// the turn epilogue (`ElicitationForm`): the answer round-trips as
// `SupplyInformation` -> `information_supplied`.
//
// Answers submit as ONE command at the END (never per question): stepping is a
// render convenience over a single logical submission, so the domain still sees
// one `ElicitationAnswer` (honors the turn model — an answer opens ONE turn).
// The cross-step draft is view-ephemeral (like unsent composer text): it carries
// no domain meaning until submitted, so it lives OUTSIDE the event log. But
// view-ephemeral is not the same as "component-lifetime": the draft is persisted
// per REQUEST ID through app/formDraftStore (the device-store seam), so it
// SURVIVES a remount (the compact transcript split can move this card across a
// conditionally-rendered boundary) AND a reload (F94/F96) — and is cleared the
// moment the answer is durably supplied (by the host) or the form is skipped.
//
// `multi_choice` is the general case that kills the agent-invented "Båda": you
// tick as many as apply. A free-text escape is a companion optional `field_text`
// ("Annat…"), never a sentinel option. No question is required (D47): the form
// is HELP, not a demand, so Continue and Submit are never blocked and a short
// hint says so once per form. Skip all never traps the author (forgiveness).
//
// Pure/props-driven except its own view-ephemeral state (the draft + the current
// question index, which resets to the first unanswered question on remount): an `InformationRequestEntry` + callbacks in, a card out; it
// computes no domain state and fetches nothing. The control render is an
// EXHAUSTIVE match over `ChatField`.

import { exhaustSwitchCase } from "@bilda/utils";
import { Trans, useLingui } from "@lingui/react/macro";
import { type ReactNode, useState } from "react";
import {
    clearFormDraft,
    type FormDraft,
    readFormDraft,
    setDraftSingle,
    toggleDraftMulti,
    writeFormDraft,
} from "../../../app/formDraftStore.js";
import { Button } from "../../Button/Button.js";
import { Checkbox } from "../../Checkbox/Checkbox.js";
import { Icon } from "../../Icon/Icon.js";
import { Input } from "../../Input/Input.js";
import { RadioGroup } from "../../RadioGroup/RadioGroup.js";
import { SegmentedControl } from "../../SegmentedControl/SegmentedControl.js";
import { Text } from "../../Text/Text.js";
import { VisuallyHidden } from "../../VisuallyHidden/VisuallyHidden.js";
import {
    type AnsweredFieldSummary,
    type ChatField,
    type ChatFieldAnswer,
    FALLBACK_FIELD_KEY,
    type FormSourceOption,
    type InformationRequestEntry,
    type InformationRequestStatus,
    OWN_ANSWER_FIELD_KEY,
} from "../types.js";
import "./InformationRequestCard.css";
import {
    clampQuestionIndex,
    collectAnswers,
    type FormQuestion,
    firstUnansweredIndex,
    toQuestions,
} from "./formQuestions.js";
import { SourcePicker } from "./SourcePicker.js";

// How roomy the controls render. `compact` (the chat default) packs short
// single-choice sets into a `SegmentedControl` and uses small inputs — economy
// for the narrow chat column; `full` (a future desktop/view host) uses a
// vertical `RadioGroup` and roomier inputs. ONE renderer, one field match — the
// density only picks the control chrome (agent-form-primitive-design §4).
type FormDensity = "compact" | "full";

export type InformationRequestCardProps = {
    readonly entry: InformationRequestEntry;
    // Submit the typed answers. Absent ⇒ the Submit button is not offered (no
    // dead button) — the host wires this to the `supply_information` command.
    readonly onSubmit?: (answers: readonly ChatFieldAnswer[]) => void;
    // Skip the form (the opt-out valve). Optional — absent still collapses the
    // card locally (client-only `dismissed`); a durable dismiss is a follow-on.
    readonly onDismiss?: () => void;
    // Control roominess. Defaults to `compact` (the chat column).
    readonly density?: FormDensity;
    // The library's sources, offered by a `field_sources` question (D42).
    // Projected in by the host; the agent never lists them itself.
    readonly sources?: readonly FormSourceOption[];
    readonly className?: string;
};

// The per-field draft state (`FormDraft`) is defined in app/formDraftStore, the
// request-id-keyed device-store seam that persists it EPHEMERALLY (survives a
// remount AND a reload, cleared on answer/skip) — see that module's header for
// why the draft's lifetime belongs to the REQUEST, not the component instance.

// The resting-state word for the card's lifecycle status.
const StatusLabel = ({
    status,
}: {
    status: InformationRequestStatus;
}): ReactNode => {
    switch (status.type) {
        case "pending":
            return <Trans>Awaiting your answer</Trans>;
        case "answered":
            return <Trans>Answered</Trans>;
        case "dismissed":
            return <Trans>Skipped</Trans>;
        default:
            return exhaustSwitchCase(status);
    }
};

export const InformationRequestCard = ({
    entry,
    onSubmit,
    onDismiss,
    density = "compact",
    sources = [],
    className,
}: InformationRequestCardProps) => {
    const { t } = useLingui();
    // Seed the draft from the request-id-keyed device-store (host-style, in the
    // mount initializer — mirrors ContextSideCard/contextCardFold). A remount
    // (compact split) or a reload re-seeds from the SAME key, so the draft is
    // RECOVERED, not reset (F94/F96).
    const [draft, setDraft] = useState<FormDraft>(() =>
        readFormDraft(entry.id),
    );
    // Optimistic resting state: set the instant the author submits/skips, before
    // the round-trip re-projects. `undefined` ⇒ defer to the projected status.
    const [localStatus, setLocalStatus] = useState<
        InformationRequestStatus | undefined
    >(undefined);
    // F113: the answered read-back nested in THIS card is collapsed by default —
    // a "Visa svar" disclosure keeps the transcript scannable. View-ephemeral: it
    // carries no domain meaning and needn't survive a reload.
    const [answersOpen, setAnswersOpen] = useState(false);

    const status = localStatus ?? entry.status;
    const resting = status.type !== "pending";
    const titleId = `${entry.id}-title`;

    const steps = entry.steps;
    const answers = entry.answers ?? [];
    const questions = toQuestions(steps);
    const questionCount = questions.length;
    // The current question is view-ephemeral (D42): it carries no domain
    // meaning, so it is NOT persisted. A remount/reload reopens on the first
    // question still without an answer in the (persisted) draft.
    const [questionIndex, setQuestionIndex] = useState(() =>
        firstUnansweredIndex(questions, draft),
    );
    const index = clampQuestionIndex(questionIndex, questionCount);
    const goTo = (next: number): void =>
        setQuestionIndex(clampQuestionIndex(next, questionCount));
    const stepped = questionCount > 1;

    // Each change computes the next draft via the pure store transition, sets it,
    // and writes it through the seam in the SAME handler (host-style, like
    // contextCardFold's toggles — never a buried effect). The write persists the
    // draft under the request id so a subsequent remount/reload recovers it.
    const setSingle = (key: string, value: string): void => {
        const next = setDraftSingle(draft, key, value);
        setDraft(next);
        writeFormDraft(entry.id, next);
    };
    const toggleMulti = (key: string, value: string): void => {
        const next = toggleDraftMulti(draft, key, value);
        setDraft(next);
        writeFormDraft(entry.id, next);
    };

    const submit = (): void => {
        if (onSubmit === undefined) return;
        onSubmit(collectAnswers(steps, draft, sources));
        // The draft is NOT dropped here: the answer only reaches the event log
        // once the host's `supplyInformation` round-trip succeeds, and the host
        // clears the draft then. Clearing on click lost the answers whenever
        // that round-trip failed (e.g. a dev-server restart) and the form came
        // back pending on reload.
        setLocalStatus({ type: "answered" });
    };
    const skip = (): void => {
        onDismiss?.();
        clearFormDraft(entry.id);
        setLocalStatus({ type: "dismissed" });
    };

    const current = questions[index];
    const onLast = index === questionCount - 1;

    return (
        // A labelled group, not a landmark — the transcript's `role="log"` is the
        // landmark; this is a titled region within it that AT reads as a unit.
        <section
            className={`bilda-info-request${className ? ` ${className}` : ""}`}
            aria-labelledby={titleId}
        >
            <header className="bilda-info-request__head">
                <h3 id={titleId} className="bilda-info-request__title">
                    <Text as="span" size="sm" weight="medium">
                        {entry.title}
                    </Text>
                </h3>
                {stepped && !resting ? (
                    <Text as="span" size="xs" tone="dim">
                        <span aria-hidden="true">
                            {index + 1} / {questionCount}
                        </span>
                        <VisuallyHidden>
                            <Trans>
                                Question {index + 1} of {questionCount}
                            </Trans>
                        </VisuallyHidden>
                    </Text>
                ) : null}
            </header>
            {entry.note !== undefined ? (
                <Text size="sm" tone="dim" className="bilda-info-request__note">
                    {entry.note}
                </Text>
            ) : null}
            {/* D47: the form is help, not a demand. Said once per form. */}
            {resting ? null : (
                <Text size="xs" tone="dim" className="bilda-info-request__note">
                    <Trans>
                        Answer as best you can. Skip anything you're unsure of.
                    </Trans>
                </Text>
            )}

            {resting ? (
                answers.length > 0 ? (
                    <AnsweredSummary
                        summary={answers}
                        open={answersOpen}
                        onToggle={() => setAnswersOpen((shown) => !shown)}
                    />
                ) : null
            ) : current !== undefined ? (
                // Keyed per question so its own view-ephemeral state (the
                // free-text escape's open/closed) resets between questions.
                <QuestionFields
                    key={current.key}
                    question={current}
                    draft={draft}
                    density={density}
                    sources={sources}
                    onSetSingle={setSingle}
                    onToggleMulti={toggleMulti}
                />
            ) : null}

            <footer className="bilda-info-request__foot">
                {resting ? (
                    <Text as="span" size="xs" tone="dim">
                        <StatusLabel status={status} />
                    </Text>
                ) : stepped ? (
                    <div className="bilda-info-request__nav">
                        <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={skip}
                            aria-label={t`Skip this form`}
                        >
                            <Trans>Skip all</Trans>
                        </Button>
                        <div className="bilda-info-request__nav-advance">
                            {index > 0 ? (
                                <Button
                                    type="button"
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => goTo(index - 1)}
                                >
                                    <Trans>Back</Trans>
                                </Button>
                            ) : null}
                            {onLast ? (
                                onSubmit !== undefined ? (
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant="primary"
                                        onClick={submit}
                                    >
                                        <Trans>Submit</Trans>
                                    </Button>
                                ) : null
                            ) : (
                                // Never blocked (D47): Continue with a blank
                                // answer IS skipping the question.
                                <Button
                                    type="button"
                                    size="sm"
                                    variant="primary"
                                    onClick={() => goTo(index + 1)}
                                >
                                    <Trans>Continue</Trans>
                                </Button>
                            )}
                        </div>
                    </div>
                ) : (
                    <>
                        {onSubmit !== undefined ? (
                            <Button
                                type="button"
                                size="sm"
                                variant="primary"
                                onClick={submit}
                            >
                                <Trans>Submit</Trans>
                            </Button>
                        ) : null}
                        <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={skip}
                            aria-label={t`Skip this question`}
                        >
                            <Trans>Skip</Trans>
                        </Button>
                    </>
                )}
            </footer>
        </section>
    );
};

// F113: the settled form's answers, nested in the SAME card behind a collapsed
// "Visa svar" disclosure — the form + its answers read as ONE card, not two. The
// structured read-back (`AnsweredFieldSummary[]`) is resolved upstream in
// `summarizeElicitationAnswers` (choice values already mapped to option labels);
// this only renders it. Reserved-key fields (the free-text escape / durable
// fallback) carry a synthesized inert-English label — localized here at the edge
// by key, exactly as the pending card does, so no English leaks into a
// non-English UI. Open/closed is view-ephemeral state owned by the card.
const AnsweredSummary = ({
    summary,
    open,
    onToggle,
}: {
    readonly summary: readonly AnsweredFieldSummary[];
    readonly open: boolean;
    readonly onToggle: () => void;
}): ReactNode => {
    const { t } = useLingui();
    const labelFor = (item: AnsweredFieldSummary): string =>
        item.key === OWN_ANSWER_FIELD_KEY || item.key === FALLBACK_FIELD_KEY
            ? t`Your answer`
            : item.label;
    return (
        <div className="bilda-info-request__answers">
            <button
                type="button"
                className="bilda-info-request__answers-toggle"
                aria-expanded={open}
                onClick={onToggle}
            >
                <Icon
                    name={open ? "chevron-down" : "chevron-right"}
                    size="xs"
                />
                <Text as="span" size="sm" tone="dim">
                    {open ? (
                        <Trans>Hide answers</Trans>
                    ) : (
                        <Trans>Show answers</Trans>
                    )}
                </Text>
            </button>
            {open ? (
                <dl className="bilda-info-request__answers-list">
                    {summary.map((item) => (
                        <div
                            key={item.key}
                            className="bilda-info-request__answers-row"
                        >
                            <dt className="bilda-info-request__answers-label">
                                <Text as="span" size="sm" weight="medium">
                                    {labelFor(item)}
                                </Text>
                            </dt>
                            <dd className="bilda-info-request__answers-value">
                                <Text as="span" size="sm" tone="dim">
                                    {item.display}
                                </Text>
                            </dd>
                        </div>
                    ))}
                </dl>
            ) : null}
        </div>
    );
};

// One question's fields as a labelled list, under its step's title/note when
// the question opens that step (so a multi-field step's context shows once).
const QuestionFields = ({
    question,
    draft,
    density,
    sources,
    onSetSingle,
    onToggleMulti,
}: {
    readonly question: FormQuestion;
    readonly draft: FormDraft;
    readonly density: FormDensity;
    readonly sources: readonly FormSourceOption[];
    readonly onSetSingle: (key: string, value: string) => void;
    readonly onToggleMulti: (key: string, value: string) => void;
}): ReactNode => {
    const { t } = useLingui();
    const { step } = question;
    // The free-text escape stays one click away (G14): an always-open text box
    // here competed with the composer below the card. View-ephemeral; an
    // already-typed answer keeps it open.
    const [ownAnswerOpen, setOwnAnswerOpen] = useState(false);
    const collapsed = (field: ChatField): boolean =>
        field.key === OWN_ANSWER_FIELD_KEY &&
        !ownAnswerOpen &&
        (draft.single[field.key] ?? "") === "";
    return (
        <>
            {question.firstOfStep && step.title !== undefined ? (
                <Text
                    as="span"
                    size="sm"
                    weight="medium"
                    className="bilda-info-request__step-title"
                >
                    {step.title}
                </Text>
            ) : null}
            {question.firstOfStep && step.note !== undefined ? (
                <Text size="sm" tone="dim" className="bilda-info-request__note">
                    {step.note}
                </Text>
            ) : null}
            <ol className="bilda-info-request__fields">
                {question.fields.map((field) =>
                    collapsed(field) ? (
                        <li
                            key={field.key}
                            className="bilda-info-request__field"
                        >
                            <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                onClick={() => setOwnAnswerOpen(true)}
                            >
                                {t`Or write your own answer`}
                            </Button>
                        </li>
                    ) : (
                        <li
                            key={field.key}
                            className="bilda-info-request__field"
                        >
                            <Text
                                as="span"
                                size="sm"
                                weight="medium"
                                className="bilda-info-request__label"
                            >
                                {/* Reserved-key fields carry SYNTHESIZED labels (set
                                outside Lingui — durable fallback data, or the
                                projection-guaranteed free-text escape) — localize
                                them at the edge by key so no English leaks into a
                                non-English UI (F5 / free-text valve). */}
                                {field.key === FALLBACK_FIELD_KEY
                                    ? t`Your answer`
                                    : field.key === OWN_ANSWER_FIELD_KEY
                                      ? t`Or write your own answer`
                                      : field.label}
                            </Text>
                            {field.help !== undefined ? (
                                <Text
                                    size="xs"
                                    tone="dim"
                                    className="bilda-info-request__help"
                                >
                                    {field.help}
                                </Text>
                            ) : null}
                            <FieldControl
                                field={field}
                                draft={draft}
                                density={density}
                                sources={sources}
                                onSetSingle={onSetSingle}
                                onToggleMulti={onToggleMulti}
                                autoFocus={
                                    field.key === OWN_ANSWER_FIELD_KEY &&
                                    ownAnswerOpen
                                }
                            />
                        </li>
                    ),
                )}
            </ol>
        </>
    );
};

// A single-choice field renders as a SegmentedControl only when it is compact
// AND its options are few + short — otherwise a vertical RadioGroup (long labels
// or many options wrap badly in a segmented row). Both bring the shared a11y +
// keyboard logic; the choice is chrome only.
const SEGMENT_MAX_OPTIONS = 4;
const SEGMENT_MAX_LABEL = 16;
const isSegmentable = (
    density: FormDensity,
    options: readonly { readonly label: string }[],
): boolean =>
    density === "compact" &&
    options.length <= SEGMENT_MAX_OPTIONS &&
    options.every((o) => o.label.length <= SEGMENT_MAX_LABEL);

// A live control per field kind, built on the shared accessible primitives
// (Input / SegmentedControl / RadioGroup / Checkbox) — no hand-rolled inputs, so
// the a11y + keyboard logic comes for free. Exhaustive over `ChatField`; a new
// kind breaks compilation here until handled.
const FieldControl = ({
    field,
    draft,
    density,
    sources,
    onSetSingle,
    onToggleMulti,
    autoFocus,
}: {
    readonly field: ChatField;
    readonly draft: FormDraft;
    readonly density: FormDensity;
    readonly sources: readonly FormSourceOption[];
    readonly onSetSingle: (key: string, value: string) => void;
    readonly onToggleMulti: (key: string, value: string) => void;
    // Focus the just-revealed free-text escape (the author asked for it).
    readonly autoFocus: boolean;
}): ReactNode => {
    const { t } = useLingui();
    const inputSize = density === "compact" ? "sm" : "md";
    switch (field.type) {
        case "field_text":
            return (
                <Input
                    size={inputSize}
                    value={draft.single[field.key] ?? ""}
                    // Localize a reserved-key field's placeholder at the edge
                    // (synthesized outside Lingui — durable fallback / the
                    // projection-guaranteed free-text escape).
                    placeholder={
                        field.key === FALLBACK_FIELD_KEY
                            ? t`Type your answer`
                            : field.key === OWN_ANSWER_FIELD_KEY
                              ? t`Type your own answer`
                              : field.placeholder
                    }
                    // Focus follows the author's own click on "Or write your own answer", never on load.
                    autoFocus={autoFocus}
                    onChange={(e) => onSetSingle(field.key, e.target.value)}
                />
            );
        case "field_single_choice": {
            const value = draft.single[field.key] ?? "";
            return isSegmentable(density, field.options) ? (
                <SegmentedControl
                    size={inputSize}
                    ariaLabel={field.label}
                    value={value}
                    options={field.options.map((o) => ({
                        value: o.value,
                        label: o.label,
                    }))}
                    onChange={(v) => onSetSingle(field.key, v)}
                />
            ) : (
                <RadioGroup
                    name={field.key}
                    ariaLabel={field.label}
                    value={value}
                    options={field.options.map((o) => ({
                        value: o.value,
                        label: o.label,
                    }))}
                    onValueChange={(v) => onSetSingle(field.key, v)}
                />
            );
        }
        case "field_multi_choice": {
            const selected = draft.multi[field.key] ?? [];
            return (
                <div className="bilda-info-request__checks">
                    {field.options.map((option) => (
                        <Checkbox
                            key={option.value}
                            id={`${field.key}-${option.value}`}
                            label={option.label}
                            checked={selected.includes(option.value)}
                            onCheckedChange={() =>
                                onToggleMulti(field.key, option.value)
                            }
                        />
                    ))}
                </div>
            );
        }
        case "field_scale":
            return (
                <div className="bilda-info-request__scale">
                    <Text as="span" size="sm" tone="dim">
                        {field.minLabel ?? String(field.min)}
                    </Text>
                    <input
                        type="range"
                        aria-label={field.label}
                        min={field.min}
                        max={field.max}
                        value={draft.single[field.key] ?? String(field.min)}
                        onChange={(e) => onSetSingle(field.key, e.target.value)}
                    />
                    <Text as="span" size="sm" tone="dim">
                        {field.maxLabel ?? String(field.max)}
                    </Text>
                </div>
            );
        case "field_sources":
            return (
                <SourcePicker
                    idBase={field.key}
                    label={field.label}
                    sources={sources}
                    selected={draft.multi[field.key] ?? []}
                    onToggle={(id) => onToggleMulti(field.key, id)}
                    size={inputSize}
                />
            );
        default:
            return exhaustSwitchCase(field);
    }
};
